import { useCallback, useEffect, useRef } from 'react';

import {
  editedCellKey,
  keyAfterSelectionChange,
} from './resolve-json-seed-value';

/**
 * Whether the user has typed into the cell on show in the panel's JSON editor
 * (PSTUDIO-4415), and the way to say so. Imports React only, so the wiring can
 * be rendered under jsdom — the panel itself cannot (it pulls in the design
 * system's Lit elements).
 *
 * A REF written synchronously, read during render as a plain boolean. Not React
 * state: the editor's `onChange` arrives from a Lit event outside any React
 * batch, and the store write that follows wakes the MobX observer through
 * `useSyncExternalStore`, whose render is flushed ahead of a `setState` made a
 * line earlier (React 18.3.1). That render saw the new text with the old flag
 * and would hand the editor a re-laid-out copy of its own text. A ref written
 * before the store write is already set when that render happens.
 * `__tests__/use-edited-cell-key.test.tsx` reproduces this shape with a plain
 * `useSyncExternalStore` source and goes red when this hook is rewritten on
 * `useState`; the live panel, with MobX and the Lit event, was not run.
 *
 * It relies on one behaviour outside this file: a `value` the editor receives
 * from the outside is applied as an external transaction and does not come back
 * as `onChange` (`@uiw/react-codemirror`'s `External` annotation). Were it to,
 * merely showing a cell would write its layout to the store.
 *
 * The ref triggers no render of its own. Where the text did not change (the
 * user pasted back exactly what the cell held) nothing re-renders, and nothing
 * re-asserts the editor's `value` either; the next render of any kind reads the
 * flag.
 *
 * Released when the selection moves to another cell, never by a commit — see
 * `keyAfterSelectionChange`. Undo, a coworker's merge, a version reload, a paste
 * into the selected cell from the grid and any other write that bypasses this
 * editor do not move the selection, so after one of them the cell stays
 * "edited" and is shown as stored until it is selected again. A selection that
 * leaves a cell and comes back inside one React batch never renders the other
 * cell, so its key is not released either.
 */
export const useEditedCellKey = (
  rowId: string | undefined,
  columnId: string | undefined
): {
  isEdited: boolean;
  markEdited: (rowId: string, columnId: string) => void;
} => {
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    keyRef.current = keyAfterSelectionChange(keyRef.current, rowId, columnId);
  }, [rowId, columnId]);

  const markEdited = useCallback(
    (markedRowId: string, markedColumnId: string) => {
      keyRef.current = editedCellKey(markedRowId, markedColumnId);
    },
    []
  );

  const isEdited =
    !!rowId && !!columnId && keyRef.current === editedCellKey(rowId, columnId);

  return { isEdited, markEdited };
};
